import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { videoCategories, featuredVideos } from '@/mocks/douhuaData';

function CategorySEO({ catName }: { catName: string }) {
  useDocumentHead({
    title: `${catName} - 豆花视频免费在线观看 | 高清不缓冲`,
    description: `豆花视频${catName}频道，免费在线观看高清${catName}，汇集西瓜视频、芒果视频、丝瓜视频等优质${catName}资源，高清流畅不缓冲。`,
    keywords: `豆花视频,${catName},免费在线观看,免费${catName},高清不缓冲`,
    canonicalUrl: `https://douhua.example.com/category/${window.location.pathname.split('/').pop()}`,
  });
  return null;
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCat = videoCategories.find((c) => c.slug === slug) || videoCategories[0];
  const catVideos = featuredVideos.filter((v) => v.category === currentCat.slug);
  const displayVideos = catVideos.length > 0 ? catVideos : featuredVideos;

  const catSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCat.name} - 豆花视频`,
    description: `豆花视频${currentCat.name}频道，免费在线观看高清${currentCat.name}内容。`,
    url: `https://douhua.example.com/category/${currentCat.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <CategorySEO catName={currentCat.name} />
      <Navbar />

      <main className="pt-20 md:pt-24 pb-12">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: currentCat.name },
            ]} />
          </div>

          {/* Category Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${currentCat.icon} text-xl md:text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{currentCat.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCat.description}</p>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {videoCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  cat.slug === currentCat.slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>{cat.name}
              </Link>
            ))}
          </div>

          {/* Video Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
            {displayVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
              >
                <div className="relative w-full h-44 md:h-48 overflow-hidden">
                  <img
                    src={video.image}
                    alt={`${video.title} - 豆花视频${currentCat.name}`}
                    title={`${video.title} - 豆花视频${currentCat.name}免费在线观看`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md whitespace-nowrap">
                    {video.duration}
                  </div>
                  <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md whitespace-nowrap">
                    {video.rating}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{video.description}</p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">{video.categoryName}</span>
                    <span>{video.views} 次播放</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {displayVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-film-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的内容，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <script type="application/ld+json">{JSON.stringify(catSchema)}</script>
    </>
  );
}